import React, {
  useRef,
  useEffect,
  useImperativeHandle,
  forwardRef,
  useState,
} from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import { environment } from '../../../environments/environment';
import './SquareCashAppPay.css';

const SCRIPT_URL = environment.squareScriptUrl;

const SCRIPT_LOAD_TIMEOUT_MS = 20000;

function loadSquareScriptWithTimeout(url, timeoutMs) {
  return new Promise((resolve, reject) => {
    const done = (fn) => {
      clearTimeout(timer);
      fn();
    };
    const timer = setTimeout(() => {
      done(() =>
        reject(
          new Error(
            `Square Web Payments SDK failed to load within ${timeoutMs / 1000}s (check network or squareScriptUrl)`
          )
        )
      );
    }, timeoutMs);

    if (window.Square) {
      done(() => resolve());
      return;
    }

    const existing = document.querySelector(`script[src="${url}"]`);
    if (existing) {
      if (window.Square) {
        done(() => resolve());
        return;
      }
      existing.addEventListener(
        'load',
        () => done(() => resolve()),
        { once: true }
      );
      existing.addEventListener(
        'error',
        () =>
          done(() =>
            reject(new Error('Square script element failed to load'))
          ),
        { once: true }
      );
      queueMicrotask(() => {
        if (window.Square) {
          done(() => resolve());
        }
      });
      return;
    }

    const script = document.createElement('script');
    script.src = url;
    script.onload = () => done(() => resolve());
    script.onerror = () =>
      done(() => reject(new Error('Failed to load Square Web Payments SDK script')));
    document.head.appendChild(script);
  });
}

const SquareCashAppPay = forwardRef(function SquareCashAppPay(
  {
    applicationId = environment.squareApplicationId,
    currentStoreId = undefined,
    orderTotal = 0,
    onToken,
    onError,
  },
  ref
) {
  // Determine the effective location id to use for Square Payments.
  const effectiveLocationId = currentStoreId;

  const cashAppPayRef = useRef(null);
  const paymentsRef = useRef(null);
  const paymentRequestRef = useRef(null);
  const onTokenRef = useRef(onToken);
  const onErrorRef = useRef(onError);
  const [ready, setReady] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [initError, setInitError] = useState(null);
  const [storedToken, setStoredToken] = useState(null);

  onTokenRef.current = onToken;
  onErrorRef.current = onError;

  useEffect(() => {
    let mounted = true;

    const loadScript = async () => {
      // Guard: don't initialize Cash App Pay with a zero or negative order total
      if (!orderTotal || orderTotal <= 0) {
        setReady(false);
        setInitError(null);
        setStoredToken(null);
        return;
      }

      setReady(false);
      setInitError(null);

      const amountInCents = Math.round(orderTotal);
      const amountUsd = (amountInCents / 100).toFixed(2);
      console.info('[SquareCashAppPay] init', {
        orderTotalCents: amountInCents,
        orderTotalUsd: amountUsd,
        locationId: effectiveLocationId,
        applicationIdSuffix: applicationId
          ? String(applicationId).slice(-8)
          : null,
      });

      try {
        await loadSquareScriptWithTimeout(SCRIPT_URL, SCRIPT_LOAD_TIMEOUT_MS);

        if (!mounted) return;

        // Initialize Square Payments
        const payments = window.Square.payments(
          applicationId,
          effectiveLocationId
        );
        paymentsRef.current = payments;

        // Create Payment Request (orderTotal is minor units / cents from apiPricingData.totalWithTip)
        const amountFormatted = (amountInCents / 100).toFixed(2);

        const paymentRequest = payments.paymentRequest({
          countryCode: 'US',
          currencyCode: 'USD',
          total: {
            amount: amountFormatted,
            label: 'Total',
          },
        });
        paymentRequestRef.current = paymentRequest;

        // Initialize Cash App Pay with Payment Request
        const referenceId = `cash-app-pay-${Date.now()}`;

        // Check if Cash App Pay is available
        const cashAppPayAvailable = await payments
          .cashAppPay(paymentRequest, {
            redirectURL: window.location.href,
            referenceId,
          })
          .catch((error) => {
            console.error('[SquareCashAppPay] Square SDK error from cashAppPay():', error);
            const reason = error?.message || error?.toString() || 'unknown reason';
            throw new Error(
              `Cash App Pay is not available. Square SDK: ${reason}`
            );
          });

        const cashAppPay = cashAppPayAvailable;

        // Attach Cash App Pay button to container
        await cashAppPay.attach('#sq-cash-app-pay-container');

        // Set up event listener for when user completes payment through QR code
        cashAppPay.addEventListener('ontokenization', (event) => {
          const { tokenResult, error } = event.detail;

          if (error) {
            setIsProcessing(false);
            onErrorRef.current?.(error);
          } else if (tokenResult && tokenResult.status === 'OK') {
            // Token received - show processing overlay while parent processes the payment
            setIsProcessing(true);
            setStoredToken(tokenResult.token);
            onTokenRef.current?.(tokenResult.token);
          } else {
            const err = new Error(
              tokenResult?.errors?.[0]?.message || 'Tokenization failed'
            );
            setIsProcessing(false);
            onErrorRef.current?.(err);
          }
        });

        cashAppPayRef.current = cashAppPay;
        setReady(true);
        setInitError(null);
      } catch (err) {
        // More descriptive error messages
        let errorMessage = 'Failed to initialize Cash App Pay';
        if (err.message) {
          errorMessage = err.message;
        }
        const lower = (err.message || '').toLowerCase();
        if (lower.includes('not available')) {
          errorMessage =
            'Cash App Pay is not available for this location or in sandbox mode';
        }
        if (
          lower.includes('location') ||
          lower.includes('region') ||
          lower.includes('unavailable')
        ) {
          errorMessage =
            'Cash App Pay is not available for your region or this Square location. It is US-only and must be enabled on your Square location.';
        }
        if (lower.includes('merchant')) {
          errorMessage =
            'Cash App Pay is not enabled for this merchant account';
        }

        console.error('[SquareCashAppPay] init failed', {
          message: err.message,
          orderTotalCents: Math.round(orderTotal),
          locationId: effectiveLocationId,
        });

        setInitError(errorMessage);
        onErrorRef.current?.(err);
      }
    };

    loadScript();

    return () => {
      mounted = false;
      try {
        if (cashAppPayRef.current?.destroy) {
          cashAppPayRef.current.destroy();
        }
      } catch (e) {
        // Error handled silently
      }
    };
  }, [applicationId, effectiveLocationId, orderTotal]);

  useImperativeHandle(
    ref,
    () => ({
      tokenize: async () => {
        // If token already exists (from previous tokenization or callback), return it
        if (storedToken) {
          return storedToken;
        }

        if (!cashAppPayRef.current) {
          throw new Error('Cash App Pay not initialized');
        }

        try {
          // Don't show processing overlay while user is interacting with Cash App Pay interface
          // The tokenize() call will open the QR code for user to scan
          const result = await cashAppPayRef.current.tokenize();

          // Token received - parent component will handle showing loading state during API call
          if (result.status === 'OK') {
            setStoredToken(result.token);
            // Call onToken callback - parent component will handle the token processing and show loading
            onTokenRef.current?.(result.token);
            return result.token;
          }

          const message =
            (result.errors && result.errors[0] && result.errors[0].message) ||
            'Cash App Pay tokenization failed';
          const err = new Error(message);
          setIsProcessing(false);
          onErrorRef.current?.(err);
          throw err;
        } catch (error) {
          // Don't show processing overlay for errors - user cancelled or error occurred
          setIsProcessing(false);
          onErrorRef.current?.(error);
          throw error;
        }
      },
      isReady: () => ready,
    }),
    [ready, storedToken]
  );

  if (!applicationId || !effectiveLocationId) {
    return (
      <Box sx={{ padding: '20px', textAlign: 'center' }}>
        <Typography color="error" sx={{ fontFamily: 'Jost' }}>
          Cash App Pay configuration is missing. Please check your environment
          settings or provide a currentStoreId.
        </Typography>
      </Box>
    );
  }

  if (!orderTotal || orderTotal <= 0) {
    return (
      <Box sx={{ padding: '20px', textAlign: 'center' }}>
        <Typography sx={{ fontFamily: 'Jost', fontSize: '14px', color: '#666' }}>
          Order total is not ready yet. Cash App Pay will appear after your total
          is calculated.
        </Typography>
      </Box>
    );
  }

  if (initError) {
    return (
      <Box sx={{ padding: '20px', textAlign: 'center' }}>
        <Typography color="error" sx={{ fontFamily: 'Jost', fontSize: '14px' }}>
          {initError}
        </Typography>
        <Typography
          sx={{
            fontFamily: 'Jost',
            fontSize: '12px',
            color: '#666',
            mt: 1,
          }}
        >
          Cash App Pay may not be available in your region or for this location.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ position: 'relative' }}>
      {!ready && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '40px 20px',
            gap: 2,
          }}
        >
          <CircularProgress size={32} sx={{ color: environment.mainColor }} />
          <Typography
            sx={{
              fontFamily: 'Jost',
              fontSize: '14px',
              color: '#666',
            }}
          >
            Loading Cash App Pay...
          </Typography>
        </Box>
      )}

      {/* Cash App Pay button will be mounted here */}
      <div
        id="sq-cash-app-pay-container"
        style={{
          minHeight: ready ? '60px' : '0px',
          opacity: ready ? 1 : 0,
          transition: 'opacity 0.3s ease',
        }}
      />

      {/* Processing Overlay */}
      {isProcessing && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 10000,
          }}
        >
          <Box
            sx={{
              backgroundColor: '#fff',
              padding: '32px',
              borderRadius: '16px',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              minWidth: '300px',
            }}
          >
            <CircularProgress
              size={48}
              sx={{ color: environment.mainColor, marginBottom: '16px' }}
            />
            <Typography
              sx={{
                fontFamily: 'Jost',
                fontSize: '18px',
                fontWeight: 600,
                color: '#1A2341',
                textAlign: 'center',
                marginBottom: '8px',
              }}
            >
              Processing Cash App Payment...
            </Typography>
            <Typography
              sx={{
                fontFamily: 'Jost',
                fontSize: '14px',
                color: '#7A869A',
                textAlign: 'center',
              }}
            >
              Please wait while we securely process your payment
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
});

export default SquareCashAppPay;
